import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { HttpResponsesHandlersFactory } from "@bbwt/modules/data-service";
import { PagedCrudService } from "@features/grid";
import { FormEventPayload, FormEventsDTO } from "../dto/formEventsDTO";
import { BooleanFilter, IQueryCommand, NumberFilter, StringFilter } from "@features/filter";
import { FormEventType } from "../components/form-events/event-type";
import { FormIODefinition } from "@features/bb-formio";
import { Subject } from "rxjs";
import { UserHelperService } from "@main/users/user-helper.service";
import { EpisodeService } from "@project/episode/episode.service";
import { UserLogin, UserService } from "@main/users";
import { MiProModules } from "@mipro/classes/mipro-modules";
import { GoverningBodyService } from "@project/governingbody/governingbody.service";
import { OrganizationService } from "@main/companies";
import { CoreRole } from "@main/roles";

@Injectable()
export class FormEventsService extends PagedCrudService<FormEventsDTO> {
    public readonly url = "api/form-events";
    public readonly entityTitle = "Form Events";
    public governingBodyId: string;
    public organizationId: string;
    public isGovgBodyPage: boolean;
    public orgSelfRegistrationURL:string;
    static readonly TriggerEntityFormEvent = "TriggerEntityFormEvent";

    private eventSubject = new Subject<FormEventPayload>();
    readonly event$ = this.eventSubject.asObservable();

    triggerFormEvent(payload: FormEventPayload): void {
        this.eventSubject.next(payload);
    }

    async getCurrentFormEvent(formEventType: FormEventType, orgSelfRegistrationURL:string = null): Promise<FormEventsDTO | null> {
       var hashedGoverningBodyId = this.userHelperService.hashedGoverningBodyId;
       var hashedOrganizationId = this.userHelperService.hashedOrganizationId;
     
       if (!hashedOrganizationId  && orgSelfRegistrationURL){
        var org = await this.organizationService.getBySelfRegistrationUrl(orgSelfRegistrationURL);
         if (org == null) {
                return null;
         }
         const hasModuleFormEnabled = org.enabledModules.find(x => x.moduleName === MiProModules.Forms).enabled;
         var roleIdGenPat = (await  this.governingBodyService.getRole(org.governingBodyId, CoreRole.GenericPatientLogin))?.id;

         if (hasModuleFormEnabled && roleIdGenPat){
            const formEvent = await this.getFormEvent(org.governingBodyId, org.id, formEventType, roleIdGenPat);
            return formEvent;
         }
       }

       if (!this.userHelperService.hasModuleEnabled(MiProModules.Forms) && formEventType !== FormEventType.CreateTestOrder) {
           return null;
       }

       if (this.userHelperService.isPatient && formEventType !== FormEventType.BookAppointment) {
           return null;
       }

       const roleId = this.userHelperService.isPatient
           ? (await this.governingBodyService.getRole(hashedGoverningBodyId,CoreRole.Player))?.id
           : this.episodeService.getPriorityRole((<UserLogin>this.userService.currentUser)?.roles)?.id;

        if (!roleId) return null;

        const formEvent = await this.getFormEvent(hashedGoverningBodyId, hashedOrganizationId, formEventType, roleId);
        return formEvent;
    }

    public extendQueryCommand(queryCommand: IQueryCommand) {
        queryCommand.filters = queryCommand.filters || [];
        queryCommand.filters.push(new StringFilter("governingBodyId", this.governingBodyId));

        if (this.organizationId) {
            queryCommand.filters.push(new StringFilter("organizationId", this.organizationId));
        }

        queryCommand.filters.push(new BooleanFilter("isGovBodyPage", this.isGovgBodyPage));
    }

    constructor(
        http: HttpClient,
        handlersFactory: HttpResponsesHandlersFactory,
        private userHelperService: UserHelperService,
        private episodeService: EpisodeService,
        private userService: UserService,
        private governingBodyService: GoverningBodyService,
        private organizationService : OrganizationService
    ) {
        super(http, handlersFactory);
    }

    getDefinitionsForEvents(governingBodyId: string, organizationId: string): Promise<FormIODefinition[]> {
        return this.handleRequest<FormIODefinition[]>(
            this.http.get<FormIODefinition[]>(`${this.url}/${governingBodyId}/${organizationId}/get-definitions-for-events`),
            this.handlersFactory.getDefault()
        );
    }

    getAssignedUserTypesForEvent(
        governingBodyId: string,
        organizationId: string,
        eventType: FormEventType,
        currentformEventId: string
    ): Promise<string[]> {
        return this.handleRequest<string[]>(
            this.http.get<string[]>(
                `${this.url}/${governingBodyId}/${organizationId}/user-types-for-event/${eventType}/${currentformEventId}`
            ),
            this.handlersFactory.getDefault()
        );
    }

    getFormEvent(governingBodyId: string, organizationId: string, eventType: FormEventType, roleId: string): Promise<FormEventsDTO> {
        return this.handleRequest<FormEventsDTO>(
            this.http.get<FormEventsDTO>(`${this.url}/${governingBodyId}/${organizationId}/get-form-events/${eventType}/${roleId}`),
            this.handlersFactory.getDefault()
        );
    }

    async create(FormEventsData: FormEventsDTO): Promise<FormEventsDTO> {
        return await this.handleRequest<FormEventsDTO>(
            this.http.post<FormEventsDTO>(`${this.url}/add-form-event`, FormEventsData),
            this.handlersFactory.getDefault()
        );
    }

    async update(formEventId: number | string, item: FormEventsDTO): Promise<FormEventsDTO> {
        return await this.handleRequest<FormEventsDTO>(
            this.http.post<FormEventsDTO>(`${this.url}/update-form-event/${formEventId}`, item),
            this.handlersFactory.getForUpdate(this.entityTitle)
        );
    }
}
